import Button from "@/components/Button";
import { useMutation } from "@apollo/client";
import { Address, PersonAddressType, Person } from "@/types";
import { FieldPathByValue, FormProvider, useForm, useFormContext } from "react-hook-form";
import CountryDropdown from "@/components/AddressAutoComplete/CountryDropdown";
import AddressAutocompleteInput from "@/components/AddressAutoComplete/AddressAutocompleteInput";
import { ControlledTextField as TextField } from "@/components/TextField";
import StateDropdown from "@/components/AddressAutoComplete/StateInput";
import { capitalize, mapValues, trim } from "lodash";
import { UPDATE_ADDRESSES_MUTATION, AddressUpdate } from "@/features/person/queries";
import { ApolloError } from "@apollo/client/errors";
import useAddressAutocomplete from "@/hooks/useAddressAutocompelte";
import PostalCodeInput from "@/components/AddressAutoComplete/PostalCodeInput";
import { useEffect, useRef, useCallback, useState } from "react";
import IconButton from "@/components/IconButton";

interface AddressEditFormProps {
  personId: string;
  addresses?: Address[];
  onCancel(): void;
  onSuccess(updatedPerson: Partial<Person>): void;
  onError(error: string): void;
}

const addressDefaultValues = {
  address1: "",
  address2: "",
  city: "",
  state: "",
  postalCode: "",
  countryCode: "",
  isVerified: false
};

const defaultValues = {
  home: addressDefaultValues,
  secondary: addressDefaultValues
};

type AddressFieldValues = typeof addressDefaultValues;

type FieldValues = { home: AddressFieldValues; secondary: AddressFieldValues };

function toInput(values: FieldValues): AddressUpdate[] {
  const addresses: AddressUpdate[] = [];

  const { isVerified: homeIsVerified, ...homeStringFields } = values.home;
  const trimmedHomeFields = mapValues(homeStringFields, trim);

  addresses.push({
    ...trimmedHomeFields,
    isVerified: homeIsVerified,
    addressType: "home"
  });

  const { isVerified: secondaryIsVerified, ...secondaryStringFields } = values.secondary;
  const trimmedSecondaryFields = mapValues(secondaryStringFields, trim);

  if (!!secondaryStringFields.address1) {
    addresses.push({
      ...trimmedSecondaryFields,
      isVerified: secondaryIsVerified,
      addressType: "secondary"
    });
  }

  return addresses;
}

function AddressForm({ prefix, onDelete }: { prefix: PersonAddressType; onDelete?(): void }) {
  const { control, watch, setValue } = useFormContext<FieldValues>();
  const previousCountryRef = useRef<string>("");

  const labelPrefix = capitalize(prefix);
  const name = useCallback(
    (n: keyof typeof addressDefaultValues) => [prefix, n].join(".") as FieldPathByValue<FieldValues, string>,
    [prefix]
  );
  const { isUSA, ...autocompleteProps } = useAddressAutocomplete({
    mapName: name
  });

  // Watch the country field to detect changes
  const currentCountry = watch(name("countryCode"));

  // Reset other address fields when country changes
  useEffect(() => {
    // Skip reset on initial load (when previousCountryRef is empty)
    if (previousCountryRef.current && previousCountryRef.current !== currentCountry) {
      // Reset all address fields except countryCode
      setValue(name("address1"), "");
      setValue(name("address2"), "");
      setValue(name("city"), "");
      setValue(name("state"), "");
      setValue(name("postalCode"), "");
      setValue(name("isVerified"), false as any);
    }
    // Update the ref with the current country
    previousCountryRef.current = currentCountry;
  }, [currentCountry, setValue, name]);

  // Customize labels based on prefix
  const streetAddressLabel = prefix === "secondary" ? "Alternate Mailing Street Address" : "Home Street Address";
  const addressLine2Label = prefix === "secondary" ? "Street Address Line 2" : "Primary Street Address Line 2";

  return (
    <div className="flex">
      <div role="form" title={labelPrefix} className="py-4 w-full">
        <div className="flex sm:gap-2 gap-3 sm:flex-row flex-col justify-evenly">
          <div className="flex">
            <div className="w-full">
              <CountryDropdown control={control} name={name("countryCode")} required />
            </div>
          </div>

          <div className="flex-grow">
            <AddressAutocompleteInput
              control={control}
              name={name("address1")}
              required
              label={streetAddressLabel}
              {...autocompleteProps}
            />
          </div>

          <div className="flex-grow flex items-end gap-2">
            <TextField control={control} name={name("address2")} className="flex-grow" label={addressLine2Label} />
            {prefix === "secondary" && onDelete && (
              <IconButton
                name="delete"
                onClick={onDelete}
                className="mb-1"
                ariaLabel="Delete alternate mailing address"
                data-testid="delete-secondary-address"
              />
            )}
          </div>
        </div>

        <div className="pt-3 flex sm:gap-2 gap-3 sm:flex-row flex-col">
          <TextField control={control} name={name("city")} className="flex-col flex-[5]" label="City" required />

          <div className="flex-[1]">
            <StateDropdown control={control} name={name("state")} isUSA={isUSA} required={isUSA} />
          </div>

          <PostalCodeInput
            control={control}
            name={name("postalCode")}
            isUSA={isUSA}
            className="flex-[1]"
            required={isUSA}
          />
        </div>
      </div>
    </div>
  );
}

function initializeValues(addresses: (Address & { __typename?: string })[]) {
  let homeAddress;
  let secondaryAddress;

  for (let { __typename, id, ...address } of addresses) {
    if (address.addressType === "home") {
      homeAddress = address;
    } else if (address.addressType === "secondary") {
      secondaryAddress = address;
    }
  }

  const addressDefault = { ...addressDefaultValues, countryCode: "USA" };
  return {
    home: homeAddress ? homeAddress : addressDefault,
    secondary: secondaryAddress ? secondaryAddress : addressDefault
  };
}

export default function AddressEditForm({
  personId,
  addresses = [],
  onCancel,
  onSuccess,
  onError
}: AddressEditFormProps) {
  const [mutate] = useMutation(UPDATE_ADDRESSES_MUTATION);

  // Check if there's an initial secondary address
  const hasInitialSecondaryAddress = addresses.some((addr) => addr.addressType === "secondary");

  const form = useForm<FieldValues>({
    defaultValues,
    values: initializeValues(addresses),
    mode: "onBlur"
  });
  const {
    handleSubmit,
    formState: { isValid, isSubmitting },
    setValue
  } = form;

  const [showSecondaryAddress, setShowSecondaryAddress] = useState(hasInitialSecondaryAddress);

  const handleDeleteSecondaryAddress = () => {
    // Reset secondary address fields to default values
    setValue("secondary.address1", "");
    setValue("secondary.address2", "");
    setValue("secondary.city", "");
    setValue("secondary.state", "");
    setValue("secondary.postalCode", "");
    setValue("secondary.countryCode", "");
    setValue("secondary.isVerified", false);
    // Hide the secondary address form
    setShowSecondaryAddress(false);
  };

  const handleAddSecondaryAddress = () => {
    // Initialize with default values
    setValue("secondary.address1", "");
    setValue("secondary.address2", "");
    setValue("secondary.city", "");
    setValue("secondary.state", "");
    setValue("secondary.postalCode", "");
    setValue("secondary.countryCode", "USA");
    setValue("secondary.isVerified", false);
    // Show the secondary address form
    setShowSecondaryAddress(true);
  };

  async function submit(data: FieldValues) {
    try {
      const result = await mutate({
        variables: { input: { id: personId, addresses: toInput(data) } }
      });
      if (result.data?.updatePerson.success) {
        const { addresses, contactAddress, contactAddressId } = result.data.updatePerson.person;

        // Always use the contactAddressId returned from the API response, since it may change
        // automatically (e.g., fallback to "home" if "secondary" is removed or was preferred).
        onSuccess({
          addresses,
          contactAddress,
          contactAddressId
        });
      } else {
        onError(result.data?.updatePerson.message || "Failed to update address");
      }
    } catch (e) {
      onError((e as ApolloError)?.message);
    }
  }

  return (
    <FormProvider {...form}>
      <div data-testid="address-edit-form">
        <AddressForm prefix="home" />
        {showSecondaryAddress ? (
          <AddressForm prefix="secondary" onDelete={handleDeleteSecondaryAddress} />
        ) : (
          <div className="py-4">
            <Button variant="filledTonal" onClick={handleAddSecondaryAddress} data-testid="add-secondary-address">
              Add Alternate Mailing Address
            </Button>
          </div>
        )}

        <div className="pt-4 flex flex-wrap justify-end gap-3 items-center">
          <Button variant="text" onClick={onCancel}>
            Cancel
          </Button>

          <Button disabled={!isValid || isSubmitting} onClick={handleSubmit(submit)}>
            Save
          </Button>
        </div>
      </div>
    </FormProvider>
  );
}
